import { ReactNode, useEffect, useMemo, useState } from 'react';
import * as R from 'remeda';
import {
  Box,
  Chip,
  Collapse,
  Stack,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { useIntl } from '../ComponentsContext';

const DESCRIPTION_MAX_LENGTH = 175;
const DESCRIPTION_MAX_WIDTH = 700;

const SCROLL_THROTTLE_MS = 150;

export const HeaderTitle = ({
  hasScrolled,
  title,
}: {
  hasScrolled: boolean;
  title: string;
}) => {
  return (
    <Typography
      variant={hasScrolled ? 'h4' : 'h3'}
      noWrap
      data-cy="header-title"
    >
      {title}
    </Typography>
  );
};

export type HeaderProps = {
  breadcrumbs: ReactNode;
  tabs?: ReactNode;
  scrollableDivRef: React.RefObject<HTMLDivElement>;
  buttons?: ReactNode[] | ((hasScrolled: boolean) => ReactNode[]);
  title: string | ((hasScrolled: boolean) => ReactNode);
  description: ReactNode;
  beta?: boolean;
  deprecated?: boolean;
  internal?: boolean;
};

export const Header = ({
  breadcrumbs,
  tabs,
  scrollableDivRef,
  buttons,
  title,
  description,
  beta,
  deprecated,
  internal,
}: HeaderProps) => {
  const [hasScrolled, setHasScrolled] = useState(false);
  const [expandedDescription, setExpandedDescription] = useState(false);

  const intl = useIntl();

  // Throttle scroll handling (leading + trailing edge), matching lodash's
  // `throttle(fn, SCROLL_THROTTLE_MS)`. Memoized so the funnel (and its stable
  // `call`/`cancel`) persists across renders instead of being recreated each
  // time. The handler reads the latest `hasScrolled` via the functional state
  // updater, so it doesn't need to close over it.
  const { call: handleScroll, cancel: cancelHandleScroll } = useMemo(
    () =>
      R.funnel(
        () => {
          if (scrollableDivRef.current) {
            const scrollY = scrollableDivRef.current.scrollTop;
            setHasScrolled(wasScrolled =>
              wasScrolled ? scrollY > 0 : scrollY > 170
            );
          }
        },
        {
          triggerAt: 'both',
          minQuietPeriodMs: SCROLL_THROTTLE_MS,
          maxBurstDurationMs: SCROLL_THROTTLE_MS,
        }
      ),
    [scrollableDivRef]
  );

  useEffect(() => {
    const div = scrollableDivRef.current;
    if (div) {
      div.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (div) {
        div.removeEventListener('scroll', handleScroll);
      }
      cancelHandleScroll();
    };
  }, [handleScroll, cancelHandleScroll, scrollableDivRef]);

  return (
    <Stack
      direction="row"
      id="queue-settings-header"
      sx={{
        borderBottom: t => `1px solid ${t.palette.divider}`,
        backgroundColor: t => t.palette.background.paper,
      }}
    >
      <Stack
        width={1}
        px={4}
        mx="auto"
        direction="row"
        alignItems="center"
        overflow="hidden"
      >
        <Stack
          pt={2}
          pb={hasScrolled || !tabs ? 2 : 0}
          spacing={hasScrolled ? 0 : 1}
          flexGrow={1}
          minWidth={0}
          sx={{
            transition: t =>
              t.transitions.create(['padding'], {
                duration: 300,
              }),
          }}
        >
          {!!breadcrumbs && (
            <Collapse in={!hasScrolled} unmountOnExit>
              <Stack direction="row" alignItems="center" gap={1.5}>
                {breadcrumbs}
              </Stack>
            </Collapse>
          )}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            overflow="hidden"
            gap={2}
          >
            <Stack
              minWidth={0}
              flexGrow={1}
              flexShrink={1}
              flexBasis={0}
              spacing={0.5}
            >
              <Stack
                direction="row"
                alignItems="baseline"
                minWidth={0}
                overflow="hidden"
              >
                {R.isString(title) ? (
                  <HeaderTitle hasScrolled={hasScrolled} title={title} />
                ) : (
                  title(hasScrolled)
                )}
                {beta && (
                  <Chip
                    label={intl.formatMessage({
                      id: 'component.betaChip.label',
                    })}
                    size="small"
                    color="warning"
                    sx={{ ml: 2 }}
                  />
                )}
                {deprecated && (
                  <Chip
                    label={intl.formatMessage({
                      id: 'component.deprecatedChip.label',
                    })}
                    size="small"
                    color="warning"
                    sx={{ ml: 2 }}
                  />
                )}
                {internal && (
                  <Tooltip
                    title={intl.formatMessage({
                      id: 'component.internalChip.tooltip',
                    })}
                  >
                    <Chip
                      label={intl.formatMessage({
                        id: 'component.internalChip.label',
                      })}
                      size="small"
                      color="transparent"
                      sx={{ ml: 2, flexShrink: 0, alignSelf: 'center' }}
                    />
                  </Tooltip>
                )}
              </Stack>
              <Collapse in={!hasScrolled}>
                <Box
                  width="auto"
                  display="flex"
                  flexGrow={0}
                  alignSelf="flex-start"
                  maxWidth={DESCRIPTION_MAX_WIDTH}
                >
                  {R.isString(description) ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="div"
                    >
                      {description.length >= DESCRIPTION_MAX_LENGTH &&
                      !expandedDescription ? (
                        <>
                          <Box component="span">{`${description.slice(0, DESCRIPTION_MAX_LENGTH)}...`}</Box>
                          <Box
                            component="span"
                            color="text.disabled"
                            onClick={() => setExpandedDescription(true)}
                            ml={1}
                            sx={{ cursor: 'pointer' }}
                          >
                            {intl.formatMessage({
                              id: 'containers.settings.queues.queue.settings.expandDescription',
                            })}
                          </Box>
                        </>
                      ) : (
                        <span>{description}</span>
                      )}
                    </Typography>
                  ) : (
                    // It is a ReactNode | null - display as-is.
                    // span is needed tho - for correct rendering of the space between the text and dynamic elements in translations
                    <Box
                      component="span"
                      sx={{ color: t => t.palette.text.secondary }}
                    >
                      {description}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </Stack>
            {buttons ? (
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
                flexShrink={0}
                flexWrap="nowrap"
              >
                {R.isArray(buttons) ? buttons : buttons(hasScrolled)}
              </Stack>
            ) : null}
          </Stack>
          {tabs ? <Collapse in={!hasScrolled}>{tabs}</Collapse> : null}
        </Stack>
      </Stack>
    </Stack>
  );
};
